import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import RequireAuth from "../components/RequireAuth";

const API_BASE = "https://eluvent.com/api";

const ATOMS = [
  { symbol: "H", color: "#ffffff", radius: 0.25 },
  { symbol: "C", color: "#333333", radius: 0.35 },
  { symbol: "N", color: "#3050f8", radius: 0.35 },
  { symbol: "O", color: "#ff0d0d", radius: 0.35 },
  { symbol: "P", color: "#ff8000", radius: 0.45 },
  { symbol: "S", color: "#ffff30", radius: 0.45 },
  { symbol: "Cl", color: "#1ff01f", radius: 0.4 },
];

const MoleculeBuilderContent = () => {
  const [formula, setFormula] = useState("H2O");
  const [atoms, setAtoms] = useState([]);
  const [bonds, setBonds] = useState([]);
  const [aiResult, setAiResult] = useState("");
  const [loading, setLoading] = useState(false);

  const loadMolecule = async () => {
    setLoading(true);
    setAiResult("");
    try {
      const res = await axios.post(`${API_BASE}/visual/chemistry/parse`, { formula }, { withCredentials: true });
      const molecule = res.data.molecule;
      if (molecule && molecule.atoms) {
        const newAtoms = molecule.atoms.map((a, i) => ({
          id: a.id || i,
          position: a.position || [(Math.random()-0.5)*3, (Math.random()-0.5)*3, (Math.random()-0.5)*3],
          symbol: a.symbol,
        }));
        const newBonds = (molecule.bonds || []).map((b, i) => ({
          id: i,
          atom1: b.atom1_id,
          atom2: b.atom2_id,
          type: b.order === 2 ? "double" : b.order === 3 ? "triple" : "single",
        }));
        setAtoms(newAtoms);
        setBonds(newBonds);
        setAiResult(`✅ ${res.data.formula}\nGeometry: ${molecule.geometry || "unknown"}\nPolar: ${molecule.is_polar ? "Yes" : "No"}\nMass: ${molecule.molecular_mass} g/mol`);
      }
    } catch {
      setAiResult("❌ Could not load molecule.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: 20, fontFamily: "'Nunito', sans-serif" }}>
      <Link to="/visual-lab">← Back to Visual Lab</Link>
      <h1>⚗️ Molecule Builder</h1>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <input value={formula} onChange={e => setFormula(e.target.value)} placeholder="Formula (e.g., H2O, CO2)" style={{ padding: "8px 14px", borderRadius: 20, border: "1px solid #e2e8f0" }} />
        <button onClick={loadMolecule} disabled={loading} style={{ padding: "10px 16px", background: "#7c3aed", color: "white", border: "none", borderRadius: 20, fontWeight: 700, cursor: "pointer" }}>
          {loading ? "Loading…" : "🔍 Load Molecule"}
        </button>
      </div>
      <div style={{ display: "flex", gap: 12 }}>
        <div style={{ flex: 1, height: 500, border: "1px solid #e2e8f0", borderRadius: 12, background: "#f8fafc" }}>
          <Canvas camera={{ position: [0,0,5] }}>
            <ambientLight intensity={0.7} />
            <directionalLight position={[5,5,5]} />
            <OrbitControls />
            {atoms.map(atom => {
              const data = ATOMS.find(a => a.symbol === atom.symbol) || ATOMS[0];
              return (
                <mesh key={atom.id} position={atom.position}>
                  <sphereGeometry args={[data.radius, 32, 32]} />
                  <meshStandardMaterial color={data.color} />
                  <Html distanceFactor={10}><div style={{color:"black",fontWeight:700}}>{atom.symbol}</div></Html>
                </mesh>
              );
            })}
            {bonds.map(bond => {
              const a = atoms.find(x => x.id === bond.atom1);
              const b = atoms.find(x => x.id === bond.atom2);
              if (!a || !b) return null;
              const mid = [(a.position[0]+b.position[0])/2,(a.position[1]+b.position[1])/2,(a.position[2]+b.position[2])/2];
              const length = Math.sqrt((a.position[0]-b.position[0])**2+(a.position[1]-b.position[1])**2+(a.position[2]-b.position[2])**2);
              return <mesh key={bond.id} position={mid}><cylinderGeometry args={[0.08,0.08,length,8]} /><meshStandardMaterial color={bond.type==="double"?"#0000ff":"#666"} /></mesh>;
            })}
          </Canvas>
        </div>
        <div style={{ width: 250, background: "white", borderRadius: 16, padding: 16 }}>
          <h3>🧪 Molecule Info</h3>
          {aiResult && <p style={{ fontSize: 13, color: "#334155", whiteSpace: "pre-line" }}>{aiResult}</p>}
        </div>
      </div>
    </div>
  );
};

export default function MoleculeBuilder() {
  return <RequireAuth><MoleculeBuilderContent /></RequireAuth>;
}